package presentation.viewmodel

import presentation.viewmodel.AppViewModel as CoreAppViewModel
import presentation.state.AppState
import ui.theme.ThemeMode
import domain.model.PRViewMode

/**
 * Compose-specific wrapper for the platform-agnostic AppViewModel
 * This bridges the gap between the core business logic and Compose UI state management
 * 
 * Note: This class should NOT contain @Composable functions. 
 * State observation should be handled in the UI layer.
 */
class ComposableAppViewModel(
    private val coreViewModel: CoreAppViewModel
) {
    
    // Expose the current state (not reactive, for immediate access)
    val currentState: AppState get() = coreViewModel.state.value
    
    // Expose the StateFlow for reactive observation in UI
    val stateFlow get() = coreViewModel.state
    
    // Delegate all operations to the core ViewModel
    fun refreshPullRequests() = coreViewModel.refreshPullRequests()
    fun addAction(title: String, description: String, url: String, icon: String?) = 
        coreViewModel.addAction(title, description, url, icon)
    fun removeAction(actionId: String) = coreViewModel.removeAction(actionId)
    fun changeTheme(newTheme: ThemeMode) = coreViewModel.changeTheme(newTheme)
    fun login() = coreViewModel.login()
    fun logout() = coreViewModel.logout()
    fun processOAuthCallback() = coreViewModel.processOAuthCallback()
    fun setShowAddActionModal(show: Boolean) = coreViewModel.setShowAddActionModal(show)
    fun setShowThemeModal(show: Boolean) = coreViewModel.setShowThemeModal(show)
    fun clearError() = coreViewModel.clearError()
    
    // Organization diagnosis methods
    fun setShowOrganizationDiagnosis(show: Boolean) = coreViewModel.setShowOrganizationDiagnosis(show)
    fun runOrganizationDiagnosis() = coreViewModel.runOrganizationDiagnosis()
    fun reauthorizeGitHubApp() = coreViewModel.reauthorizeGitHubApp()
    
    // Enhanced features methods (CLEAN architecture)
    fun loadEnhancedFeatures() = coreViewModel.loadEnhancedFeatures()
    fun refreshEnhancedFeatures() = coreViewModel.refreshEnhancedFeatures()
    fun changeViewMode(viewMode: PRViewMode) = coreViewModel.changeViewMode(viewMode)
    fun loadEnhancedPRs(viewMode: PRViewMode) = coreViewModel.loadEnhancedPRs(viewMode)
    
    // Settings modal methods
    fun setShowSettingsModal(show: Boolean) = coreViewModel.setShowSettingsModal(show)
    
    // Clean up resources when ViewModel is no longer needed
    fun onCleared() = coreViewModel.onCleared()
}
